import {
  Button,
  IconButton,
  Card,
  Container,
  Dialog,
  Stack,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Grid,
  Tooltip,
  Paper,
  Tabs,
  Tab,
  Autocomplete,
  Divider,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { ChangeEvent, MouseEvent, useCallback, useEffect, useState, useMemo } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import moment from 'moment';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Page from 'src/components/Page';
import SearchTextField from 'src/components/SearchTextField';
import FilterDrawer from 'src/components/FilterDrawer';
import CheckboxList from 'src/components/CheckboxList';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useActivityDefinitionService, usePlanDefinitionService } from './api/DefinitionService';
import { getOrganizationsByIds } from 'src/careflow/ph-core/admin/organization/service/organizations';
import { ActivityDefinition, PlanDefinition } from 'src/careflow/ph-core/fhir-client/resources/resources';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { debounce } from 'src/utils/timers';
import DatePickerMoment from 'src/components/DatePickerMoment';
import { BreadCrumbTitle, GridItem, GridSection } from 'src/components/CustomModal';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import CancelButton from 'src/careflow/common/CancelButton';
import {
  createTransactionBundle,
  downloadJson,
  downloadCsv,
  mapResourcesWithFhirPath,
} from 'src/utils/fhirExport';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';

const planInitialState: Partial<PlanDefinition> = {
  resourceType: 'PlanDefinition',
  name: '',
  title: '',
  description: '',
  status: 'active',
  useContext: [],
  effectivePeriod: { start: moment().toISOString(), end: undefined },
  action: [],
  goal: [],
  topic: [],
};

const EXPORT_COLUMNS = {
  ID: 'id',
  Name: 'name',
  Title: 'title',
  Status: 'status',
  Description: 'description',
  Organization: "useContext.where(code.code='workflow').value.reference",
  'Last Updated': 'meta.lastUpdated',
};

export default function PlanDefinitionList() {
  const { getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();
  const { getFiltered, create, update } = usePlanDefinitionService();
  const { getFiltered: getActivities } = useActivityDefinitionService();

  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [activitiesForActions, setActivitiesForActions] = useState<ActivityDefinition[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('general');

  const [searchValue, setSearchValue] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [planInForm, setPlanInForm] = useState<Partial<PlanDefinition>>(planInitialState);
  const [editingPlan, setEditingPlan] = useState<PlanDefinition | null>(null);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanDefinition | null>(null);

  const permissions = {
    isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.PLAN_DEFINITION.ADD] }),
    isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.PLAN_DEFINITION.EDIT] }),
  };

  const { valueSets: [pathWaysValueSet, careCoordinationNeedValueSet, planDefinitionTypeVS, planStatusVS, goalCodesVS] } =
    useValueSetsByIdentifiers([
      'ph-path-ways',
      'care-coordination-need',
      'ph-plan-definition-type',
      'ph-plan-definition-status',
      'ph-goal-codes',
    ]);
  const orgIds = useMemo(
    () => (currentUser?.organizations || (currentUser?.organization_uuid ? [currentUser.organization_uuid] : [])),
    [currentUser?.organizations?.join(','), currentUser?.organization_uuid]
  );

  const planStatusOptions = useMemo(() => {
    return planStatusVS?.asList?.() || [];
  }, [planStatusVS]);

  const fetchOrganizations = useCallback(async () => {
    try {
      if (orgIds && orgIds.length > 0) {
        const response = await getOrganizationsByIds(orgIds);
        setOrganizations(response);
      }
    } catch (error) {
      console.error('Failed to fetch organizations', error);
    }
  }, [orgIds]);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getFiltered(orgIds);
      setPlans(response);
    } catch (error) {
      console.error('Failed to fetch plan definitions', error);
    } finally {
      setLoading(false);
    }
  }, [orgIds, getFiltered]);

  useEffect(() => {
    fetchPlans();
    fetchOrganizations();
  }, [fetchPlans, fetchOrganizations]);

  useEffect(() => {
    const fetchActivitiesForActions = async () => {
      try {
        const res = await getActivities(orgIds);
        setActivitiesForActions(res);
      } catch (e) {
        console.error('Failed to fetch activities for actions', e);
      }
    };
    if (open) fetchActivitiesForActions();
  }, [open, orgIds, getActivities]);

  const handleSearchChange = useMemo(
    () => debounce((val: string) => setSearch(val), 600),
    []
  );

  const filteredPlans = useMemo(() => {
    let result = [...plans];
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter(
        (p) => p.name?.toLowerCase().includes(lowerSearch) || p.description?.toLowerCase().includes(lowerSearch)
      );
    }
    if (statusFilter.length > 0) {
      result = result.filter((p) => p.status && statusFilter.includes(p.status));
    }
    return result;
  }, [plans, search, statusFilter]);

  const handleExportMenuOpen = (event: MouseEvent<HTMLButtonElement>) => setExportAnchorEl(event.currentTarget);
  const handleExportMenuClose = () => setExportAnchorEl(null);

  const handleOpen = () => {
    setEditingPlan(null);
    setPlanInForm(planInitialState);
    setActiveSection('general');
    setOpen(true);
  };

  const handleExportTransactionBundle = () => {
    const bundle = createTransactionBundle(filteredPlans);
    const fileName = `plan-definitions-transaction-${moment().format('YYYYMMDDHHmm')}.json`;
    downloadJson(bundle, fileName);
    handleExportMenuClose();
  };

  const handleExportCsv = () => {
    const dataToExport = mapResourcesWithFhirPath(filteredPlans, EXPORT_COLUMNS);
    downloadCsv(dataToExport, `plan-definitions-${moment().format('YYYYMMDDHHmm')}.csv`);
    handleExportMenuClose();
  };

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLButtonElement>, plan: PlanDefinition) => {
    setAnchorEl(event.currentTarget);
    setSelectedPlan(plan);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setPlanInForm(planInitialState);
    setEditingPlan(null);
  };

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedPlan(null);
  }, []);

  const handleEditClick = () => {
    if (selectedPlan) {
      setEditingPlan(selectedPlan);
      setPlanInForm(selectedPlan);
      setActiveSection('general');
      setOpen(true);
    }
    handleMenuClose();
  };

  const columns = useMemo<GridColDef[]>(() => [
    { field: 'name', headerName: 'Name', flex: 1, renderCell: (params) => <Typography sx={{ fontWeight: 'bold' }}>{params.value}</Typography> },
    { field: 'description', headerName: 'Description', flex: 1.5 },
    {
      field: 'useContext',
      headerName: 'Organization',
      flex: 1,
      valueGetter: (params) => {
        const orgRef = params.row.useContext?.find((uc: any) => uc.valueReference?.reference?.includes('Organization/'))?.valueReference?.reference;
        if (!orgRef) return '';
        const orgId = orgRef.split('/').pop();
        return organizations.find((o) => o.fhirId === orgId)?.name || orgId;
      },
    },
    { field: 'status', headerName: 'Status', flex: 0.8 },
    {
      field: 'lastUpdated',
      headerName: 'Last Updated',
      flex: 1,
      valueGetter: (params) => {
        return params.row.meta?.lastUpdated ? moment(params.row.meta.lastUpdated).format('YYYY-MM-DD HH:mm') : '';
      },
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      width: 80,
      align: 'right',
      renderCell: (params) =>
        permissions.isAllowedToEdit && (
          <IconButton onClick={(e) => handleMenuOpen(e, params.row)}>
            <Iconify icon={'eva:more-vertical-fill'} width={20} height={20} />
          </IconButton>
        ),
    },
  ], [handleMenuOpen, organizations, permissions.isAllowedToEdit]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPlanInForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAction = () => {
    setPlanInForm((prev) => ({ ...prev, action: [...(prev.action || []), { definitionCanonical: '' }] }));
  };

  const handleAddGoal = () => {
    setPlanInForm((prev) => ({
      ...prev,
      goal: [
        ...(prev.goal || []),
        {
          description: { text: '' },
          addresses: [],
          category: { coding: [] },
        },
      ],
    }));
  };

  const handleAddTopic = () => {
    const defaultTopic = planDefinitionTypeVS?.asList?.()[0];

    setPlanInForm((prev) => ({
      ...prev,
      topic: [
        ...(prev.topic || []),
        {
          coding: defaultTopic
            ? [{ code: defaultTopic.code, display: defaultTopic.display, system: planDefinitionTypeVS?.getConceptSystem?.() }]
            : [],
        },
      ],
    }));
  };

  const handleSavePlan = async () => {
    try {
      setSaving(true);
      const conceptSystem = careCoordinationNeedValueSet?.getConceptSystem?.();
      const addressCodings: any[] = [];
      planInForm.goal?.forEach((g) => {
        g.addresses?.forEach((addr) => {
          addr.coding?.forEach((c) => {
            if (c.code && c.system && !addressCodings.some((ac) => ac.code === c.code && ac.system === c.system)) {
              addressCodings.push({ code: c.code, system: c.system, display: c.display });
            }
          });
        });
      });

      const otherTags = planInForm.meta?.tag?.filter((t) => t.system !== conceptSystem) || [];
      const payload = {
        ...planInForm,
        meta: { ...planInForm.meta, tag: [...otherTags, ...addressCodings] },
      };

      if (editingPlan && editingPlan.id) {
        await update(editingPlan.id, payload as PlanDefinition);
      } else {
        await create(payload as PlanDefinition);
      }
      handleClose();
      fetchPlans();
    } catch (error) {
      console.error('Failed to save plan definition', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title="Plan Definitions">
      <Container>
        <HeaderBreadcrumbs
          heading=""
          title="Plan Definition List"
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Plan Definitions' }]}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleExportMenuOpen}
                endIcon={<Iconify icon={'eva:chevron-down-fill'} />}
              >
                Export as
              </Button>
              {permissions.isAllowedToAdd && (
                <Button
                  variant="contained"
                  startIcon={<Iconify icon={'eva:plus-fill'} />}
                  onClick={handleOpen}
                >
                  New Plan
                </Button>
              )}
            </Stack>
          }
        />
        <Card sx={{ p: 2 }}>
          <Grid container spacing={2} sx={{ mb: 2, px: 1 }} justifyContent="space-between" alignItems="center">
            <Grid item xs={12} sm={4}>
              <SearchTextField
                value={searchValue}
                onChange={(event) => {
                  setSearchValue(event.target.value);
                  handleSearchChange(event.target.value);
                }}
                fullWidth
                placeholder="Search by name or description..."
              />
            </Grid>
            <Grid item xs={12} sm={4} display="flex" justifyContent="flex-end">
              <Tooltip title="Filter">
                <IconButton onClick={() => setIsFilterDrawerOpen(true)}>
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>

          <DataGrid
            autoHeight
            rows={filteredPlans}
            columns={columns}
            loading={loading}
            initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
            pageSize={pageSize}
            onPageSizeChange={(newSize) => setPageSize(newSize)}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{ '& .MuiDataGrid-columnHeader': { backgroundColor: '#f4f6f8' } }}
          />
        </Card>

        <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportMenuClose}>
          <MenuItem onClick={handleExportTransactionBundle}>
            <Iconify icon={'eva:file-text-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Bundle transaction
          </MenuItem>
          <MenuItem onClick={handleExportCsv}>
            <Iconify icon={'eva:file-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            CSV
          </MenuItem>
        </Menu>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEditClick}>
            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Edit
          </MenuItem>
        </Menu>

        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth
          maxWidth={false}
          PaperProps={{ sx: { width: '70vw', paddingX: 3, maxHeight: '90vh' } }}
        >
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'} sx={{ paddingTop: 1, gap: 1 }}>
            <Typography variant="h6">{editingPlan ? 'Edit Plan Definition' : 'New Plan Definition'}</Typography>
            <BreadCrumbTitle useSeparator title="Plan Definitions" />
            <BreadCrumbTitle useSeparator title={editingPlan ? 'Edit' : 'Create'} />
          </Box>

          <Box display="flex" sx={{ mt: 3, gap: 2 }}>
            <Paper
              elevation={0}
              sx={{ width: '250px', minWidth: '250px', backgroundColor: '#f4f6f8', borderRadius: 1, p: 2 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#212b36' }}>
                Setup Wizard
              </Typography>
              <Tabs
                orientation="vertical"
                value={activeSection}
                onChange={(_, v) => setActiveSection(v)}
                sx={{
                  '& .MuiTabs-indicator': { display: 'none' },
                  '& .MuiTab-root': {
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    minHeight: 'auto',
                    padding: '12px 16px',
                    marginBottom: '4px',
                    borderRadius: 1,
                    textTransform: 'none',
                    '&.Mui-selected': { backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 600 },
                  },
                }}
              >
                <Tab value="general" label="General Info" />
                <Tab value="timing" label="Timing" />
                <Tab value="actions" label="Actions" />
                <Tab value="goals" label="Goals" />
                <Tab value="topics" label="Topics" />
              </Tabs>
            </Paper>

            <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 'calc(90vh - 200px)', p: 1 }}>
              <Grid container justifyContent={'space-between'}>
                {activeSection === 'general' && (
                  <Grid item xs={12}>
                    <GridSection>
                      <GridItem>
                        <TextField name="name" label="Technical Name (ID)" value={planInForm.name} onChange={handleInputChange} fullWidth />
                      </GridItem>
                      <GridItem>
                        <TextField name="title" label="Display Title" value={planInForm.title} onChange={handleInputChange} fullWidth />
                      </GridItem>
                    </GridSection>
                    <GridSection>
                      <GridItem xs={12}>
                        <Autocomplete
                          value={organizations.find((o) => 
                            planInForm.useContext?.some(uc => uc.valueReference?.reference === `Organization/${o.fhirId}`)
                          ) || null}
                          options={organizations}
                          getOptionLabel={(o) => o.name || ''}
                          onChange={(_, v) => {
                            const otherContexts = planInForm.useContext?.filter(uc => !uc.valueReference?.reference?.startsWith('Organization/')) || [];
                            const newContexts = v ? [
                              ...otherContexts,
                              {
                                code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'workflow' },
                                valueReference: { reference: `Organization/${v?.fhirId}` }
                              }
                            ] : otherContexts;
                            setPlanInForm(p => ({ ...p, useContext: newContexts }));
                          }}
                          renderInput={(p) => <TextField {...p} label="Organization Owner" />}
                        />
                      </GridItem>
                    </GridSection>
                    <GridSection>
                      <GridItem xs={12}>
                        <TextField name="description" label="Description" value={planInForm.description} onChange={handleInputChange} fullWidth multiline rows={3} />
                      </GridItem>
                    </GridSection>
                    <GridSection>
                      <GridItem xs={6}>
                        <Autocomplete
                          value={planStatusOptions.find(o => o.code === planInForm.status) || null}
                          options={planStatusOptions}
                          getOptionLabel={o => o.display || ''}
                          onChange={(_, v) => setPlanInForm(p => ({ ...p, status: v?.code as any }))}
                          renderInput={p => <TextField {...p} label="Status" />}
                        />
                      </GridItem>
                    </GridSection>
                  </Grid>
                )}

                {activeSection === 'timing' && (
                  <Grid item xs={12}>
                    <GridSection mt={0}>
                      <GridItem xs={6}>
                        <DatePickerMoment
                          label="Effective Period Start"
                          value={planInForm.effectivePeriod?.start ? moment(planInForm.effectivePeriod.start) : null}
                          onChange={v => setPlanInForm(p => ({ ...p, effectivePeriod: { ...p.effectivePeriod, start: v?.toISOString() } }))}
                        />
                      </GridItem>
                      <GridItem xs={6}>
                        <DatePickerMoment
                          label="Effective Period End"
                          value={planInForm.effectivePeriod?.end ? moment(planInForm.effectivePeriod.end) : null}
                          onChange={v => setPlanInForm(p => ({ ...p, effectivePeriod: { ...p.effectivePeriod, end: v?.toISOString() } }))}
                        />
                      </GridItem>
                    </GridSection>
                  </Grid>
                )}

                {activeSection === 'actions' && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1">Plan Actions (Activities)</Typography>
                      <Button startIcon={<AddIcon />} onClick={handleAddAction} variant="outlined">Add Action</Button>
                    </Box>
                    {planInForm.action?.map((act, index) => (
                      <Grid container spacing={2} key={index} alignItems="center" sx={{ mb: 2 }}>
                        <Grid item xs={10}>
                          <Autocomplete
                            value={activitiesForActions.find(a => `ActivityDefinition/${a.name}` === act.definitionCanonical) || null}
                            options={activitiesForActions}
                            getOptionLabel={o => `${o.title} (${o.name})`}
                            onChange={(_, v) => {
                              const newList = [...planInForm.action!];
                              newList[index] = { ...newList[index], definitionCanonical: v ? `ActivityDefinition/${v.name}` : '' };
                              setPlanInForm(p => ({ ...p, action: newList }));
                            }}
                            renderInput={p => <TextField {...p} label="Activity Definition" />}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton onClick={() => setPlanInForm(p => ({ ...p, action: p.action?.filter((_, i) => i !== index) }))} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {activeSection === 'goals' && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1">Plan Goals</Typography>
                      <Button startIcon={<AddIcon />} onClick={handleAddGoal} variant="outlined">Add Goal</Button>
                    </Box>
                    {planInForm.goal?.map((g, index) => (
                      <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Stack spacing={2}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Goal #{index + 1}</Typography>
                            <IconButton size="small" onClick={() => setPlanInForm(p => ({ ...p, goal: p.goal?.filter((_, i) => i !== index) }))} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Autocomplete
                            value={goalCodesVS?.asList?.().find(c => c.code === g.description?.coding?.[0]?.code) || null}
                            options={goalCodesVS?.asList?.() || []}
                            getOptionLabel={o => o.display || ''}
                            onChange={(_, v) => {
                              const newList = [...planInForm.goal!];
                              newList[index] = {
                                ...newList[index],
                                description: {
                                  coding: v ? [{ code: v.code, display: v.display, system: goalCodesVS?.getConceptSystem?.() }] : [],
                                  text: v?.display || ''
                                }
                              };
                              setPlanInForm(p => ({ ...p, goal: newList }));
                            }}
                            renderInput={p => <TextField {...p} label="Goal Description" />}
                          />
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Autocomplete
                                multiple
                                value={(g.addresses || []).map(addr =>
                                  careCoordinationNeedValueSet?.asList?.()?.find(c => c.code === addr.coding?.[0]?.code)
                                ).filter((item): item is any => !!item)}
                                options={careCoordinationNeedValueSet?.asList?.() || []}
                                getOptionLabel={o => o.display || ''}
                                onChange={(_, v) => {
                                  const newList = [...planInForm.goal!];
                                  newList[index] = {
                                    ...newList[index],
                                    addresses: (v as any[]).map(item => ({
                                      coding: [{ code: item.code, display: item.display, system: careCoordinationNeedValueSet?.getConceptSystem?.() }],
                                      text: item.display
                                    })),
                                  };
                                  setPlanInForm(p => ({ ...p, goal: newList }));
                                }}
                                renderInput={p => <TextField {...p} label="Addresses (Needs/Conditions)" />}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Autocomplete
                                value={pathWaysValueSet?.asList?.().find((c) => c.code === g.category?.coding?.[0]?.code) || null}
                                options={pathWaysValueSet?.asList?.() || []}
                                getOptionLabel={(o) => o.display || ''}
                                onChange={(_, v) => {
                                  const newList = [...planInForm.goal!];
                                  newList[index] = {
                                    ...newList[index],
                                    category: {
                                      coding: v ? [{ code: v.code, display: v.display, system: pathWaysValueSet?.getConceptSystem?.() }] : [],
                                    },
                                  };
                                  setPlanInForm(p => ({ ...p, goal: newList }));
                                }}
                                renderInput={(p) => <TextField {...p} label="Category" />}
                              />
                            </Grid>
                          </Grid>
                          </Stack>
                        </Paper>
                    ))}
                  </Grid>
                )}

                {activeSection === 'topics' && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1">Plan Topics</Typography>
                      <Button startIcon={<AddIcon />} onClick={handleAddTopic} variant="outlined">Add Topic</Button>
                    </Box>
                    {planInForm.topic?.map((t, index) => (
                      <Grid container spacing={2} key={index} alignItems="center" sx={{ mb: 1 }}>
                        <Grid item xs={10}>
                          <Autocomplete
                            value={planDefinitionTypeVS?.asList?.().find((c) => c.code === t.coding?.[0]?.code) || null}
                            options={planDefinitionTypeVS?.asList?.() || []}
                            getOptionLabel={(o) => o.display || ''}
                            onChange={(_, v) => {
                              const newList = [...planInForm.topic!];
                              newList[index] = {
                                coding: v ? [{ code: v.code, display: v.display, system: planDefinitionTypeVS?.getConceptSystem?.() }] : [],
                              };
                              setPlanInForm((p) => ({ ...p, topic: newList }));
                            }}
                            renderInput={(p) => <TextField {...p} label="Topic Type" />}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton onClick={() => setPlanInForm((p) => ({ ...p, topic: p.topic?.filter((_, i) => i !== index) }))} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>

          <Box sx={{ p: 3 }} display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={1}>
            <CancelButton disabled={saving} onClick={handleClose} />
            <LoadingButton loading={saving} onClick={handleSavePlan} variant="contained">
              {editingPlan ? 'Save Changes' : 'Create Plan'}
            </LoadingButton>
          </Box>
        </Dialog>

        <FilterDrawer
          open={isFilterDrawerOpen}
          onCloseIconButtonClick={() => setIsFilterDrawerOpen(false)}
          onApplyButtonClick={() => setIsFilterDrawerOpen(false)}
          onClearAllButtonClick={() => setStatusFilter([])}
          title="Filters"
        >
          <Stack spacing={3} sx={{ p: 3 }}>
            <CheckboxList
              title="Status"
              options={planStatusOptions}
              keyExtractor={(item) => item.code}
              labelExtractor={(item) => item.display}
              externalCheckedItems={planStatusOptions.filter((o) => statusFilter.includes(o.code))}
              onChange={(items) => setStatusFilter(items.map((i) => i.code))}
            />
          </Stack>
        </FilterDrawer>
      </Container>
    </Page>
  );
}